/** What the catalog records' changesets share (plan §9.6–§9.8: the activity, the activity
 *  category and the itinerary run on one changeset core): their refusals, and the re-key
 *  question's answer on the wire (O11 — the activity's and the itinerary's `custom_id`).
 *
 *  - 409 `CatalogRecordDriftError` → `CatalogDriftError`: some change's `from` no longer
 *    matches the stored value (`drifted[]`, `gone: true` for a row deleted meanwhile);
 *    nothing was written.
 *  - 400 `CatalogRecordValidationError` (and a field-keyed 403, `code: forbidden`) →
 *    `CatalogChangesetValidationError`: `errors` keyed `"<entity>:<id>:<field>"`,
 *    `"options.<key>"`, `"description"` or `"changes"`; `code: disabled` — the
 *    `ConsoleCatalogEditEnabled` kill switch is off. Nothing was written.
 *
 *  Both carry what the records core recognises by shape (`drifted`; `code` + `errors`), so
 *  the ledger rebases a drift per field and files a refusal under its fields. */
import type { RecordChangeset } from '@/features/records/editor/types';

import { ConsoleApiError } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

/** The re-key question's answer key (`options.rekey_import_matching`, O11). */
export const REKEY_IMPORT_MATCHING = 'rekey_import_matching';
/** The Custom ID a Re-key confirms (`options.rekey_import_matching_to`). */
export const REKEY_IMPORT_MATCHING_TO = 'rekey_import_matching_to';

/** The re-key options of a catalog changeset (the activity's, the itinerary's): the answer,
 *  with the Custom ID it confirms beside it when the editor names one (null confirms a
 *  removal). Undefined while the question is unanswered — a target is never sent alone. */
export function rekeyOptions(
  options: RecordChangeset['options'],
): { rekey_import_matching: 'rekey' | 'cancel'; rekey_import_matching_to?: string | null } | undefined {
  const rekey = options?.[REKEY_IMPORT_MATCHING];
  if (rekey !== 'rekey' && rekey !== 'cancel') return undefined;
  const to = options?.[REKEY_IMPORT_MATCHING_TO];
  return { rekey_import_matching: rekey, ...(typeof to === 'string' || to === null ? { rekey_import_matching_to: to } : {}) };
}

export type CatalogRecordErrorCode = Schemas['CatalogRecordErrorCode'];
export type CatalogRecordFieldError = Schemas['CatalogRecordFieldError'];
export type CatalogRecordDriftedField = Schemas['CatalogRecordDriftedField'];
export type CatalogRecordChangeOutcome = Schemas['CatalogRecordChangeOutcome'];
export type CatalogRecordConflict = Schemas['CatalogRecordConflict'];
export type CatalogRecordVerdict = Schemas['CatalogRecordVerdict'];
export type CatalogRecordFeed = Schemas['CatalogRecordFeed'];

/** 409: a `from` no longer matches the stored value. Nothing was written. */
export class CatalogDriftError extends ConsoleApiError {
  constructor(
    status: number,
    body: ConsoleApiError['body'],
    message: string,
    public readonly drifted: CatalogRecordDriftedField[],
  ) {
    super(status, body, message);
  }
}

/** 400 (or the field-keyed 403): the changeset was refused. Nothing was written. */
export class CatalogChangesetValidationError extends ConsoleApiError {
  constructor(
    status: number,
    body: ConsoleApiError['body'],
    message: string,
    public readonly code: CatalogRecordErrorCode,
    public readonly errors: Record<string, CatalogRecordFieldError[]>,
  ) {
    super(status, body, message);
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Re-type a catalog changeset call's ConsoleApiError by its body; anything else passes
 *  through. A 403 without a field-keyed body (no manage permission) stays a plain error. */
export function toCatalogChangesetError(e: unknown): unknown {
  if (!(e instanceof ConsoleApiError)) return e;
  const body: unknown = e.body;
  if (!isPlainObject(body)) return e;
  if (e.status === 409 && body.code === 'drift' && Array.isArray(body.drifted)) {
    return new CatalogDriftError(
      e.status,
      e.body,
      e.message,
      body.drifted as CatalogRecordDriftedField[],
    );
  }
  if (
    (e.status === 400 || e.status === 403) &&
    typeof body.code === 'string' &&
    isPlainObject(body.errors)
  ) {
    return new CatalogChangesetValidationError(
      e.status,
      e.body,
      e.message,
      body.code as CatalogRecordErrorCode,
      body.errors as Record<string, CatalogRecordFieldError[]>,
    );
  }
  return e;
}
